import * as THREE from 'three'
import { COLORS } from '../config'
import type { HitEvent } from '../feedback'

interface TimedEffect {
  object: THREE.Object3D
  life: number
  maximumLife: number
  velocity: THREE.Vector3
  kind:
    | 'tracer'
    | 'impact'
    | 'explosion'
    | 'slash'
    | 'dust'
    | 'sparkBurst'
    | 'ring'
    | 'flashCore'
    | 'arc'
    | 'pillar'
    | 'debris'
    | 'muzzle'
  /** Per-instance velocities, for debris batches integrated in `update`. */
  instanceVelocities?: THREE.Vector3[]
  /** Angular speed applied to rotation.y each second. */
  spin?: number
  /** Scale multiplier reached at end of life (lerped from 1). */
  growTo?: number
}

/** A callback deferred by simulation seconds — chained detonations etc. */
interface ScheduledEffect {
  delay: number
  fire: () => void
}

export class Effects {
  private readonly effects: TimedEffect[] = []
  private readonly scheduled: ScheduledEffect[] = []
  private readonly dustCapacity = 24
  private readonly availableDust: THREE.Group[] = []
  private readonly dustGeometry = new THREE.DodecahedronGeometry(1, 0)
  private readonly dustMatrix = new THREE.Matrix4()
  private readonly dustPosition = new THREE.Vector3()
  private readonly dustRotation = new THREE.Quaternion()
  private readonly dustScale = new THREE.Vector3()
  private dustCreated = 0
  /** One shared flash lamp, snapped to the most recent muzzle flash. Lights
   * are per-frame cost whether or not they move, so one is the budget. */
  private muzzleLight: THREE.PointLight | null = null
  private muzzleLightRemaining = 0

  constructor(
    private readonly scene: THREE.Scene,
    private readonly random: () => number = Math.random,
  ) {}

  /**
   * One landed hit, routed by weapon and severity. This is the other half of
   * the request's core fix: `Game.damageEnemy` now forwards the weapon, the
   * post-multiplier damage and the direct flag, and this dispatch makes a
   * rifle graze and a blade direct hit physically different pictures:
   *
   *   rifle   → 9-spark burst + flat shock ring (small, 0.22 s)
   *   missile → fireball core + expanding shock ring + 12 sparks (0.5 s)
   *   blade   → full slash arc + vertical cut line + 16 sparks along the cut
   *   direct  → adds a near-white flash core to any of the above
   *
   * Instance counts per weapon are constants so the FX draw-call budget stays
   * a fixed, assertable number per event (see harness/capture.mjs).
   */
  hit(event: Pick<HitEvent, 'weapon' | 'tier' | 'direct'>, position: THREE.Vector3, attackYaw = 0): void {
    switch (event.weapon) {
      case 'rifle':
        this.rifleHit(position, event.direct)
        break
      case 'missile':
        this.missileHit(position, event.direct)
        break
      case 'blade':
        this.bladeHit(position, attackYaw, event.direct)
        break
      default:
        break
    }
  }

  private rifleHit(position: THREE.Vector3, direct: boolean): void {
    this.sparkBurst(position, 9, COLORS.hot, 0.22, 0.8)
    this.ring(position, 0.32, 0.62, COLORS.hot, 0.16, 2.1)
    if (direct) this.flashCore(position, 0.5, 0.14)
  }

  private missileHit(position: THREE.Vector3, direct: boolean): void {
    this.sparkBurst(position, 12, COLORS.ember, 0.4, 1.5)
    this.fireball(position, 0.95, 0.5)
    this.ring(position, 0.55, 1.05, COLORS.ember, 0.42, 3.4)
    if (direct) this.flashCore(position, 0.8, 0.18)
  }

  private bladeHit(position: THREE.Vector3, attackYaw: number, direct: boolean): void {
    this.slashArc(position, attackYaw, direct)
    this.cutLine(position, 0.3)
    this.sparkBurst(position, 16, COLORS.iceBright, 0.34, 1.6)
    this.flashCore(position, direct ? 1.15 : 0.8, direct ? 0.22 : 0.16)
    if (direct) this.ring(position, 0.6, 1.1, COLORS.iceBright, 0.34, 3.0)
  }

  /**
   * The moment an ACS breaks: two expanding rings (one flat, one upright), a
   * light pillar and a spark shell, all readable from their first frame so a
   * freeze-frame capture of the instant still shows the event.
   */
  staggerBreak(position: THREE.Vector3): void {
    // Bolder than any weapon hit on purpose: this is the game's biggest
    // moment, and thin rings additively drawn over the mid-grey deck were
    // measurably faint (the pixel gate caught it). Wide bands, big pillar.
    this.ring(position, 0.9, 1.75, COLORS.ice, 0.55, 3.3)
    this.ring(position, 0.7, 1.3, COLORS.iceBright, 0.46, 3.6, true)
    this.pillar(position, COLORS.iceBright, 0.5)
    this.sparkBurst(position, 12, COLORS.ice, 0.44, 2.3)
    this.flashCore(position, 1.25, 0.22)
  }

  /**
   * Death, by weight class. A skirmisher pops: core flash, one ring, a shower
   * of tumbling debris. A bulwark dies in stages — two internal detonations
   * walk up its frame, then the main blast with a double ring and heavier,
   * slower debris. Previously both were the same two meshes at different
   * scales, which is why kills read as identical.
   */
  killBlast(position: THREE.Vector3, heavy: boolean): void {
    if (!heavy) {
      this.explosion(position, 1.25)
      this.ring(position, 0.7, 1.2, COLORS.ember, 0.5, 3.6)
      this.debrisBurst(position, 12, 9.5, 0.95)
      this.smoke(position, 2)
      return
    }
    // Chained: the pre-detonations sell internal structural failure before the
    // frame lets go. Delays are simulation seconds, so a hit-stop or FX freeze
    // holds the whole sequence together.
    this.flashCore(position.clone().add(new THREE.Vector3(0.9, 0.7, 0.3)), 0.55, 0.14)
    this.schedule(0.12, () => {
      this.flashCore(position.clone().add(new THREE.Vector3(-0.8, -0.9, -0.4)), 0.65, 0.14)
      this.sparkBurst(position, 9, COLORS.ember, 0.3, 1.2)
    })
    this.schedule(0.26, () => {
      this.explosion(position, 1.9)
      this.ring(position, 0.9, 1.5, COLORS.ember, 0.55, 4.2)
      this.ring(position, 0.6, 1.0, COLORS.hot, 0.4, 3.2, true)
      this.pillar(position, COLORS.hot, 0.5)
      this.debrisBurst(position, 20, 12, 1.35)
      this.smoke(position, 3)
    })
  }

  /**
   * Muzzle flash: a three-quad star at the muzzle plus one shared, pooled
   * point light. `grep muzzleFlash → 0 hits` was on the request's evidence
   * list; firing now visibly starts at the gun.
   */
  muzzleFlash(position: THREE.Vector3, direction: THREE.Vector3): void {
    const star = new THREE.InstancedMesh(
      new THREE.BoxGeometry(1, 1, 1),
      new THREE.MeshBasicMaterial({
        color: COLORS.hot,
        transparent: true,
        opacity: 0.95,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        toneMapped: false,
      }),
      3,
    )
    const matrix = new THREE.Matrix4()
    const quaternion = new THREE.Quaternion()
    const scale = new THREE.Vector3()
    const centre = new THREE.Vector3()
    for (let index = 0; index < 3; index += 1) {
      quaternion.setFromEuler(new THREE.Euler(0, 0, (index / 3) * Math.PI))
      scale.set(0.55, 0.09, 0.09)
      matrix.compose(centre, quaternion, scale)
      star.setMatrixAt(index, matrix)
    }
    star.instanceMatrix.needsUpdate = true
    const group = new THREE.Group()
    group.add(star)
    group.position.copy(position)
    group.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, -1), direction.clone().normalize())
    this.scene.add(group)
    this.effects.push({
      object: group,
      life: 0.06,
      maximumLife: 0.06,
      velocity: new THREE.Vector3(),
      kind: 'muzzle',
      growTo: 1.6,
    })

    if (!this.muzzleLight) {
      this.muzzleLight = new THREE.PointLight(COLORS.hot, 0, 7, 2)
      this.scene.add(this.muzzleLight)
    }
    this.muzzleLight.position.copy(position)
    this.muzzleLight.intensity = 22
    this.muzzleLightRemaining = 0.055
  }

  tracer(start: THREE.Vector3, end: THREE.Vector3, hostile = false): void {
    const direction = end.clone().sub(start)
    const length = direction.length()
    const geometry = new THREE.CylinderGeometry(0.045, 0.045, length, 5)
    const material = new THREE.MeshBasicMaterial({
      color: hostile ? COLORS.threat : COLORS.iceBright,
      transparent: true,
      opacity: 0.95,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      toneMapped: false,
    })
    const object = new THREE.Mesh(geometry, material)
    object.position.copy(start).add(end).multiplyScalar(0.5)
    object.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction.normalize())
    this.scene.add(object)
    this.effects.push({ object, life: 0.085, maximumLife: 0.085, velocity: new THREE.Vector3(), kind: 'tracer' })
  }

  impact(position: THREE.Vector3, hostile = false): void {
    const group = new THREE.Group()
    const color = hostile ? COLORS.threat : COLORS.hot
    const sparks = new THREE.InstancedMesh(
      new THREE.BoxGeometry(1, 1, 1),
      new THREE.MeshBasicMaterial({ color, toneMapped: false }),
      9,
    )
    const sparkMatrix = new THREE.Matrix4()
    const sparkPosition = new THREE.Vector3()
    const sparkRotation = new THREE.Quaternion()
    const sparkScale = new THREE.Vector3()
    for (let index = 0; index < 9; index += 1) {
      const length = 0.7 + this.random() * 0.7
      sparkRotation.setFromEuler(
        new THREE.Euler(this.random() * 3, this.random() * 3, this.random() * 3),
      )
      sparkScale.set(0.055, 0.055, length)
      sparkMatrix.compose(sparkPosition, sparkRotation, sparkScale)
      sparks.setMatrixAt(index, sparkMatrix)
    }
    sparks.instanceMatrix.setUsage(THREE.StaticDrawUsage)
    sparks.instanceMatrix.needsUpdate = true
    sparks.computeBoundingBox()
    sparks.computeBoundingSphere()
    group.add(sparks)
    group.position.copy(position)
    this.scene.add(group)
    this.effects.push({
      object: group,
      life: 0.34,
      maximumLife: 0.34,
      velocity: new THREE.Vector3(0, 2.2, 0),
      kind: 'impact',
    })
  }

  explosion(position: THREE.Vector3, scale = 1): void {
    const group = new THREE.Group()
    const core = new THREE.Mesh(
      new THREE.IcosahedronGeometry(1.5 * scale, 2),
      new THREE.MeshBasicMaterial({
        color: COLORS.hot,
        transparent: true,
        opacity: 1,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        toneMapped: false,
      }),
    )
    group.add(core)
    const shell = new THREE.Mesh(
      new THREE.IcosahedronGeometry(2.2 * scale, 1),
      new THREE.MeshBasicMaterial({
        color: COLORS.ember,
        wireframe: true,
        transparent: true,
        opacity: 0.75,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      }),
    )
    group.add(shell)
    group.position.copy(position)
    this.scene.add(group)
    this.effects.push({ object: group, life: 0.7, maximumLife: 0.7, velocity: new THREE.Vector3(0, 2, 0), kind: 'explosion' })
  }

  slash(origin: THREE.Vector3, yaw: number): void {
    const geometry = new THREE.TorusGeometry(5.1, 0.13, 5, 42, Math.PI * 1.15)
    const material = new THREE.MeshBasicMaterial({
      color: COLORS.iceBright,
      transparent: true,
      opacity: 0.9,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      toneMapped: false,
    })
    const object = new THREE.Mesh(geometry, material)
    object.position.copy(origin).add(new THREE.Vector3(0, 4.2, 0))
    object.rotation.set(Math.PI / 2.7, yaw - Math.PI / 2, 0.15)
    this.scene.add(object)
    this.effects.push({ object, life: 0.28, maximumLife: 0.28, velocity: new THREE.Vector3(0, 0.4, 0), kind: 'slash' })
  }

  dust(position: THREE.Vector3, drift: THREE.Vector3, intensity = 1): void {
    const group = this.acquireDust()
    const seed = this.effects.length * 1.618 + position.x * 0.17 + position.z * 0.11
    this.configureDustBatch(group, seed)
    group.position.copy(position).setY(0.18)
    group.rotation.set(0, 0, 0)
    group.scale.setScalar(0.7 + intensity * 0.35)
    this.scene.add(group)
    this.effects.push({
      object: group,
      life: 0.72,
      maximumLife: 0.72,
      velocity: drift.clone().multiplyScalar(0.18).add(new THREE.Vector3(0, 1.05, 0)),
      kind: 'dust',
    })
  }

  poolStats(): { activeDust: number; availableDust: number; dustCapacity: number } {
    return {
      activeDust: this.effects.filter((effect) => effect.kind === 'dust').length,
      availableDust: this.availableDust.length,
      dustCapacity: this.dustCapacity,
    }
  }

  update(deltaSeconds: number): void {
    for (let index = this.scheduled.length - 1; index >= 0; index -= 1) {
      const entry = this.scheduled[index]
      if (!entry) continue
      entry.delay -= deltaSeconds
      if (entry.delay <= 0) {
        this.scheduled.splice(index, 1)
        entry.fire()
      }
    }

    if (this.muzzleLight) {
      this.muzzleLightRemaining -= deltaSeconds
      if (this.muzzleLightRemaining <= 0) this.muzzleLight.intensity = 0
    }

    for (let index = this.effects.length - 1; index >= 0; index -= 1) {
      const effect = this.effects[index]
      if (!effect) continue
      effect.life -= deltaSeconds
      if (effect.life <= 0) {
        this.scene.remove(effect.object)
        if (effect.kind === 'dust') this.availableDust.push(effect.object as THREE.Group)
        else this.disposeObject(effect.object)
        this.effects.splice(index, 1)
        continue
      }
      effect.object.position.addScaledVector(effect.velocity, deltaSeconds)
      const progress = 1 - effect.life / effect.maximumLife
      if (effect.kind === 'explosion') {
        effect.object.scale.setScalar(1 + progress * 2.8)
      } else if (effect.kind === 'impact') {
        effect.object.rotation.y += deltaSeconds * 7
        effect.object.scale.setScalar(1 + progress * 1.3)
      } else if (effect.kind === 'slash') {
        effect.object.scale.setScalar(1 + progress * 0.45)
      } else if (effect.kind === 'dust') {
        effect.object.scale.multiplyScalar(1 + deltaSeconds * 2.4)
        effect.object.rotation.y += deltaSeconds * 0.8
      } else if (effect.kind === 'sparkBurst') {
        effect.object.rotation.y += deltaSeconds * (effect.spin ?? 7)
        effect.object.scale.setScalar(1 + progress * (effect.growTo ?? 1.3))
      } else if (effect.kind === 'ring') {
        effect.object.scale.setScalar(1 + progress * ((effect.growTo ?? 3) - 1))
      } else if (effect.kind === 'flashCore') {
        effect.object.scale.setScalar(1 + progress * 1.6)
      } else if (effect.kind === 'arc') {
        effect.object.scale.setScalar(1 + progress * 0.5)
      } else if (effect.kind === 'pillar') {
        effect.object.scale.y = 1 + progress * 0.8
      } else if (effect.kind === 'muzzle') {
        effect.object.scale.setScalar(1 + progress * ((effect.growTo ?? 1.6) - 1))
      } else if (effect.kind === 'debris' && effect.instanceVelocities) {
        this.integrateDebris(effect, deltaSeconds)
      }
      effect.object.traverse((child) => {
        if (!(child instanceof THREE.Mesh)) return
        const material = child.material
        if (!Array.isArray(material) && 'opacity' in material) {
          material.opacity = Math.max(0, (effect.kind === 'dust' ? 0.24 : 1) * (1 - progress))
        }
      })
    }
  }

  dispose(): void {
    this.scheduled.length = 0
    if (this.muzzleLight) {
      this.scene.remove(this.muzzleLight)
      this.muzzleLight.dispose()
      this.muzzleLight = null
    }
    const dustGroups = new Set<THREE.Group>(this.availableDust)
    for (const effect of this.effects) {
      this.scene.remove(effect.object)
      if (effect.kind === 'dust') dustGroups.add(effect.object as THREE.Group)
      else this.disposeObject(effect.object)
    }
    for (const group of dustGroups) {
      this.scene.remove(group)
      const materials = new Set<THREE.Material>()
      group.traverse((child) => {
        if (!(child instanceof THREE.Mesh)) return
        if (child instanceof THREE.InstancedMesh) child.dispose()
        if (Array.isArray(child.material)) child.material.forEach((material) => materials.add(material))
        else materials.add(child.material)
      })
      materials.forEach((material) => material.dispose())
    }
    this.dustGeometry.dispose()
    this.effects.length = 0
    this.availableDust.length = 0
    this.dustCreated = 0
  }

  private acquireDust(): THREE.Group {
    const available = this.availableDust.pop()
    if (available) return available

    if (this.dustCreated < this.dustCapacity) {
      this.dustCreated += 1
      return this.createDustGroup()
    }

    const oldestIndex = this.effects.findIndex((effect) => effect.kind === 'dust')
    const oldest = oldestIndex >= 0 ? this.effects[oldestIndex] : undefined
    if (!oldest) throw new Error('Dust pool reached an invalid state')
    this.effects.splice(oldestIndex, 1)
    this.scene.remove(oldest.object)
    return oldest.object as THREE.Group
  }

  private createDustGroup(): THREE.Group {
    const group = new THREE.Group()
    const material = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.24,
      depthWrite: false,
    })
    const batch = new THREE.InstancedMesh(this.dustGeometry, material, 6)
    batch.instanceMatrix.setUsage(THREE.DynamicDrawUsage)
    for (let index = 0; index < 6; index += 1) {
      batch.setColorAt(index, new THREE.Color(index % 2 === 0 ? 0x8b4b36 : 0x5d3b31))
    }
    if (batch.instanceColor) batch.instanceColor.needsUpdate = true
    group.add(batch)
    return group
  }

  private configureDustBatch(group: THREE.Group, seed: number): void {
    const batch = group.children[0]
    if (!(batch instanceof THREE.InstancedMesh)) throw new Error('Dust pool batch is missing')
    const material = batch.material
    if (!Array.isArray(material) && 'opacity' in material) material.opacity = 0.24
    for (let index = 0; index < batch.count; index += 1) {
      const angle = seed + index * 2.399
      const radius = 0.3 + (index % 3) * 0.1
      this.dustPosition.set(
        Math.cos(angle) * 1.1,
        0.15 + (index % 2) * 0.2,
        Math.sin(angle) * 1.1,
      )
      this.dustScale.set(radius * 1.8, radius * 0.7, radius * 1.8)
      this.dustMatrix.compose(this.dustPosition, this.dustRotation, this.dustScale)
      batch.setMatrixAt(index, this.dustMatrix)
    }
    batch.instanceMatrix.needsUpdate = true
    batch.computeBoundingBox()
    batch.computeBoundingSphere()
  }

  private disposeObject(object: THREE.Object3D): void {
    object.traverse((child) => {
      if (!(child instanceof THREE.Mesh)) return
      if (child instanceof THREE.InstancedMesh) child.dispose()
      child.geometry.dispose()
      if (Array.isArray(child.material)) child.material.forEach((material) => material.dispose())
      else child.material.dispose()
    })
  }

  private schedule(delay: number, fire: () => void): void {
    this.scheduled.push({ delay, fire })
  }

  /** Instanced spark shower: `count` thin shards in one draw call. */
  private sparkBurst(
    position: THREE.Vector3,
    count: number,
    color: number,
    life: number,
    sizeScale: number,
  ): void {
    const sparks = new THREE.InstancedMesh(
      new THREE.BoxGeometry(1, 1, 1),
      new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity: 1,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        toneMapped: false,
      }),
      count,
    )
    const matrix = new THREE.Matrix4()
    const centre = new THREE.Vector3()
    const rotation = new THREE.Quaternion()
    const scale = new THREE.Vector3()
    for (let index = 0; index < count; index += 1) {
      const length = (0.7 + this.random() * 0.7) * sizeScale
      rotation.setFromEuler(
        new THREE.Euler(this.random() * 3, this.random() * 3, this.random() * 3),
      )
      scale.set(0.055 * sizeScale, 0.055 * sizeScale, length)
      matrix.compose(centre, rotation, scale)
      sparks.setMatrixAt(index, matrix)
    }
    sparks.instanceMatrix.setUsage(THREE.StaticDrawUsage)
    sparks.instanceMatrix.needsUpdate = true
    sparks.computeBoundingBox()
    sparks.computeBoundingSphere()
    const group = new THREE.Group()
    group.add(sparks)
    group.position.copy(position)
    this.scene.add(group)
    this.effects.push({
      object: group,
      life,
      maximumLife: life,
      velocity: new THREE.Vector3(0, 2.2, 0),
      kind: 'sparkBurst',
      spin: 6,
      growTo: 1.6,
    })
  }

  /**
   * Expanding shock ring. Spawned at its base radius rather than zero so the
   * very first frame of the event already reads as a ring — freeze-frame
   * captures and low-frame-rate machines both depend on that.
   */
  private ring(
    position: THREE.Vector3,
    innerRadius: number,
    outerRadius: number,
    color: number,
    life: number,
    growTo: number,
    upright = false,
  ): void {
    const mesh = new THREE.Mesh(
      new THREE.RingGeometry(innerRadius, outerRadius, 28),
      new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity: 0.9,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        toneMapped: false,
        side: THREE.DoubleSide,
      }),
    )
    mesh.position.copy(position)
    if (!upright) mesh.rotation.x = -Math.PI / 2
    this.scene.add(mesh)
    this.effects.push({
      object: mesh,
      life,
      maximumLife: life,
      velocity: new THREE.Vector3(0, upright ? 0.4 : 0.9, 0),
      kind: 'ring',
      growTo,
    })
  }

  /** Near-white core flash: the shared "this one counted" marker for direct
   * hits, pre-detonations and the stagger break. */
  private flashCore(position: THREE.Vector3, radius: number, life: number): void {
    const mesh = new THREE.Mesh(
      new THREE.IcosahedronGeometry(radius, 1),
      new THREE.MeshBasicMaterial({
        color: COLORS.flash,
        transparent: true,
        opacity: 1,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        toneMapped: false,
      }),
    )
    mesh.position.copy(position)
    this.scene.add(mesh)
    this.effects.push({
      object: mesh,
      life,
      maximumLife: life,
      velocity: new THREE.Vector3(),
      kind: 'flashCore',
    })
  }

  /** Missile fireball: a hot core that swells and burns out. */
  private fireball(position: THREE.Vector3, radius: number, life: number): void {
    const mesh = new THREE.Mesh(
      new THREE.IcosahedronGeometry(radius, 1),
      new THREE.MeshBasicMaterial({
        color: COLORS.hot,
        transparent: true,
        opacity: 1,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        toneMapped: false,
      }),
    )
    mesh.position.copy(position)
    this.scene.add(mesh)
    this.effects.push({
      object: mesh,
      life,
      maximumLife: life,
      velocity: new THREE.Vector3(0, 1.4, 0),
      kind: 'flashCore',
    })
  }

  /**
   * The blade's slash arc: a 130° fan swung through the target, plus nothing
   * subtle about it. Full size on frame one; the growth is only +50%.
   */
  private slashArc(position: THREE.Vector3, attackYaw: number, direct: boolean): void {
    const geometry = new THREE.RingGeometry(1.4, 4.1, 24, 1, 0, Math.PI * 0.72)
    const mesh = new THREE.Mesh(
      geometry,
      new THREE.MeshBasicMaterial({
        color: direct ? COLORS.flash : COLORS.iceBright,
        transparent: true,
        opacity: 0.95,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        toneMapped: false,
        side: THREE.DoubleSide,
      }),
    )
    mesh.position.copy(position)
    mesh.rotation.set(0.35, attackYaw - Math.PI / 2, 0.5)
    this.scene.add(mesh)
    this.effects.push({
      object: mesh,
      life: 0.32,
      maximumLife: 0.32,
      velocity: new THREE.Vector3(0, 0.6, 0),
      kind: 'arc',
    })
  }

  /** Vertical cut line through the impact point. */
  private cutLine(position: THREE.Vector3, life: number): void {
    const mesh = new THREE.Mesh(
      new THREE.BoxGeometry(0.07, 8.4, 0.07),
      new THREE.MeshBasicMaterial({
        color: COLORS.flash,
        transparent: true,
        opacity: 0.95,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        toneMapped: false,
      }),
    )
    mesh.position.copy(position)
    mesh.rotation.z = 0.42
    this.scene.add(mesh)
    this.effects.push({
      object: mesh,
      life,
      maximumLife: life,
      velocity: new THREE.Vector3(0, 0.5, 0),
      kind: 'pillar',
    })
  }

  /** Rising light pillar for break / heavy-kill moments. */
  private pillar(position: THREE.Vector3, color: number, life: number): void {
    const mesh = new THREE.Mesh(
      new THREE.BoxGeometry(0.4, 10.5, 0.4),
      new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity: 0.85,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        toneMapped: false,
      }),
    )
    mesh.position.copy(position).add(new THREE.Vector3(0, 1.5, 0))
    this.scene.add(mesh)
    this.effects.push({
      object: mesh,
      life,
      maximumLife: life,
      velocity: new THREE.Vector3(0, 2.4, 0),
      kind: 'pillar',
    })
  }

  /**
   * Tumbling wreckage: one InstancedMesh, per-chunk velocities integrated with
   * gravity in `update`. Chunk tints ride in `instanceColor` against a white
   * base — never a missing vertex-colour attribute (the black-dust regression).
   */
  private debrisBurst(position: THREE.Vector3, count: number, speed: number, size: number): void {
    const debris = new THREE.InstancedMesh(
      new THREE.BoxGeometry(1, 1, 1),
      new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 1 }),
      count,
    )
    const ember = new THREE.Color(COLORS.ember)
    const metal = new THREE.Color(COLORS.metal)
    const matrix = new THREE.Matrix4()
    const rotation = new THREE.Quaternion()
    const scale = new THREE.Vector3()
    const velocities: THREE.Vector3[] = []
    for (let index = 0; index < count; index += 1) {
      const chunk = (0.16 + this.random() * 0.3) * size
      rotation.setFromEuler(
        new THREE.Euler(this.random() * 3, this.random() * 3, this.random() * 3),
      )
      scale.set(chunk, chunk * (0.5 + this.random()), chunk)
      matrix.compose(new THREE.Vector3(), rotation, scale)
      debris.setMatrixAt(index, matrix)
      debris.setColorAt(index, index % 3 === 0 ? ember : metal)
      const angle = this.random() * Math.PI * 2
      const pitch = this.random() * 0.9 + 0.25
      velocities.push(
        new THREE.Vector3(
          Math.cos(angle) * Math.cos(pitch),
          Math.sin(pitch) * 1.25,
          Math.sin(angle) * Math.cos(pitch),
        ).multiplyScalar(speed * (0.55 + this.random() * 0.7)),
      )
    }
    debris.instanceMatrix.setUsage(THREE.DynamicDrawUsage)
    debris.instanceMatrix.needsUpdate = true
    if (debris.instanceColor) debris.instanceColor.needsUpdate = true
    const group = new THREE.Group()
    group.add(debris)
    group.position.copy(position)
    this.scene.add(group)
    this.effects.push({
      object: group,
      life: 1.1,
      maximumLife: 1.1,
      velocity: new THREE.Vector3(),
      kind: 'debris',
      instanceVelocities: velocities,
    })
  }

  private readonly debrisMatrix = new THREE.Matrix4()
  private readonly debrisPosition = new THREE.Vector3()
  private readonly debrisRotation = new THREE.Quaternion()
  private readonly debrisScale = new THREE.Vector3()

  private integrateDebris(effect: TimedEffect, deltaSeconds: number): void {
    const batch = effect.object.children[0]
    if (!(batch instanceof THREE.InstancedMesh) || !effect.instanceVelocities) return
    for (let index = 0; index < batch.count; index += 1) {
      const velocity = effect.instanceVelocities[index]
      if (!velocity) continue
      velocity.y -= 21 * deltaSeconds
      batch.getMatrixAt(index, this.debrisMatrix)
      this.debrisMatrix.decompose(this.debrisPosition, this.debrisRotation, this.debrisScale)
      this.debrisPosition.addScaledVector(velocity, deltaSeconds)
      // Keep wreckage above the deck: a bounce would need restitution state,
      // a floor clamp needs none and reads almost the same at this lifetime.
      const floor = -effect.object.position.y + this.debrisScale.y * 0.5
      if (this.debrisPosition.y < floor) {
        this.debrisPosition.y = floor
        velocity.y = 0
        velocity.x *= 0.86
        velocity.z *= 0.86
      }
      this.debrisRotation.multiply(
        new THREE.Quaternion().setFromEuler(
          new THREE.Euler(deltaSeconds * 3.4, deltaSeconds * 2.1, deltaSeconds * 2.8),
        ),
      )
      this.debrisMatrix.compose(this.debrisPosition, this.debrisRotation, this.debrisScale)
      batch.setMatrixAt(index, this.debrisMatrix)
    }
    batch.instanceMatrix.needsUpdate = true
  }

  /** Smoke reusing the pooled dust batches, lifted to the blast height. */
  private smoke(position: THREE.Vector3, puffs: number): void {
    for (let index = 0; index < puffs; index += 1) {
      this.dust(
        position.clone().add(new THREE.Vector3((index - puffs / 2) * 0.8, 0, 0)),
        new THREE.Vector3(0, 3.2 + index * 0.9, 0),
        1.5,
      )
    }
  }
}
